import React, { useMemo } from 'react';
import Modal from '$/components/base/Modal';
import { trackMyIpDetailPageView } from '$/domains/myIp/tracking';
import { getMyIpDetailMedia } from '$/domains/myIp/detailMedia';
import { useMyIpCopy } from '$/pages/RoboneoMyIps/useMyIpCopy';
import MyIpDetailView from '@/pages/--roboneo/myips/components/MyIpDetailView';
import { useMyIpDetail } from '@/pages/--roboneo/myips/hooks/useMyIpDetail';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';
import { getMyIpDetailHref, getMyIpProjectHref } from '@/utils/myIp/routes';
import styles from './index.module.scss';

interface Props {
  /** 当前要展示的角色 IP；关闭后由宿主清空。 */
  ipId: string;
  onClose: () => void;
}

/**
 * 以弹框承载完整 IP 详情页内容（与独立详情路由共用 MyIpDetailView）。
 * 弹框只读：隐藏编辑、角色卡与为 TA 创作入口；关闭走右上角。
 */
export default function MyIpDetailModal({ ipId, onClose }: Props) {
  const { detail: detailCopy, unavailableReason } = useMyIpCopy();
  const session = useMyIpSession();
  const {
    refresh, loadProjects, save, ...state
  } = useMyIpDetail(ipId, session.scopeKey, session.enabled && Boolean(ipId), session.userId);
  /** 角色卡分享指向真实详情 URL；非法身份回退列表路径，避免渲染期抛错。 */
  const detailHref = useMemo(() => {
    try {
      return getMyIpDetailHref(session.paths.list, ipId);
    } catch {
      return session.paths.list;
    }
  }, [ipId, session.paths.list]);
  const roleCardShareUrl = useMemo(() => {
    if (typeof window === 'undefined') return detailHref;
    return `${window.location.origin}${detailHref}`;
  }, [detailHref]);

  return (
    <Modal
      isModalOpen
      title={detailCopy.characterSettings}
      width="calc(100vw - 48px)"
      style={{ maxWidth: 1440 }}
      modalClassName={styles.modal}
      headerStyle={{
        flexShrink: 0,
        height: 40,
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid var(--stroke-borderOverlay, rgba(255, 255, 255, 0.08))',
      }}
      maskStyle={{
        background: 'var(--background-backgroundMaskSecondary, rgba(0, 0, 0, 0.6))',
        backdropFilter: 'blur(3px)',
      }}
      close={onClose}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
    >
      <div className={styles.body}>
        <MyIpDetailView
          hideBackNavigation
          hideHeaderActions
          state={state}
          media={getMyIpDetailMedia(state.item)}
          backHref={session.paths.home}
          onPageVisible={trackMyIpDetailPageView}
          homeHref={session.paths.home}
          onRetry={refresh}
          onLoadProjects={loadProjects}
          onSave={(item, draft) => save(item, draft, session.locale)}
          locale={session.locale}
          getProjectHref={(project) => getMyIpProjectHref(session.paths.prefix, project)}
          unavailableReason={unavailableReason}
          roleCardShareUrl={roleCardShareUrl}
        />
      </div>
    </Modal>
  );
}
